import { range } from 'lodash';
import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import { ChangeHandler, InternalFieldName } from 'react-hook-form';

interface ICustomDatePickerProps {
    name: InternalFieldName;
    onChange: ChangeHandler;
    onBlur: ChangeHandler;
}
const years = range(1990, new Date().getFullYear() + 11);
const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
];
const fToken = 'MM-dd-yyyy';
const CustomDatePickerWithForwardedRef = React.forwardRef<HTMLInputElement, ICustomDatePickerProps>(({ onChange, onBlur, name }, ref) => {
    const [selectedDate, setSelectedDate] = useState<DateTime | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    useEffect(() => {
        if (!inputRef)
            return;
        if (typeof ref === 'function')
            ref(inputRef.current);
        else
            if (ref !== null)
                ref.current = inputRef.current;
        if (inputRef.current) {
            if (inputRef.current.value) {
                try {
                    if (!selectedDate) { //only set initial date if it hasn't already been set via onchange handlers
                        let date = DateTime.fromISO(inputRef.current.value);
                        setSelectedDate(date);
                    }
                } catch (err) {
                    console.debug(err);
                }
            }
        }


    }, [inputRef, ref, selectedDate]);

    //we need to intercept react hook forms on change so we can update our state we'll use to set the date picker's selected
    const internalInputChange = useCallback((event: any) => {
        if (event.target.value) {
            let date = DateTime.fromFormat(event.target.value, fToken);
            setSelectedDate(date);
        }
        onChange(event); //call the original onChange handler from hook forms
    }, [onChange])

    //this fires when you select a date in the date picker, we need to get the native setter on the input wrapper
    //and trigger it's input event which will natively fire it's on Change event and cause our internalInputChange wrapper to fire thus updating react hook forms value tracking
    const dateOnChange = useCallback((date: Date) => {
        if (inputRef.current) {
            var nativeSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, "value")?.set;
            var lxDate = DateTime.fromISO(date.toISOString());
            nativeSetter?.call(inputRef.current, lxDate.toFormat(fToken));
            inputRef.current.dispatchEvent(new Event('input', { bubbles: true }));
        }
    }, [inputRef]);

    //notice the visually hidden input, I created a dummy input here for react hook forms to hook up to, we set the date picker from that input
    return (
        <>
            <input className="visually-hidden" ref={inputRef} type="text" name={name} onChange={internalInputChange} onBlur={onBlur} />
            <DatePicker
                className="form-control"
                onChange={dateOnChange}
                value={selectedDate?.toFormat(fToken)}
                selected={selectedDate?.toJSDate()}
                renderCustomHeader={({
                    date,
                    changeYear,
                    changeMonth,
                    decreaseMonth,
                    increaseMonth,
                    prevMonthButtonDisabled,
                    nextMonthButtonDisabled,
                }) => (
                    <div
                        style={{
                            margin: 10,
                            display: "flex",
                            justifyContent: "center",
                        }}
                    >
                        <button type="button" onClick={decreaseMonth} disabled={prevMonthButtonDisabled}>
                            {"<"}
                        </button>
                        <select
                            value={date.getFullYear()}
                            onChange={({ target: { value } }) => changeYear(new Date(value).getFullYear() + 1)}
                        >
                            {years.map((option) => (
                                <option key={option} value={option}>
                                    {option}
                                </option>
                            ))}
                        </select>

                        <select
                            value={months[date.getMonth()]}
                            onChange={({ target: { value } }) =>
                                changeMonth(months.indexOf(value))
                            }
                        >
                            {months.map((option) => (
                                <option key={option} value={option}>
                                    {option}
                                </option>
                            ))}
                        </select>

                        <button type="button" onClick={increaseMonth} disabled={nextMonthButtonDisabled}>
                            {">"}
                        </button>
                    </div>

                )}
            />
        </>
    )
});
CustomDatePickerWithForwardedRef.displayName = 'CustomDatePicker';
CustomDatePickerWithForwardedRef.propTypes = {
    name: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    onBlur: PropTypes.func.isRequired
}
export const CustomDatePicker = React.memo(CustomDatePickerWithForwardedRef);
